<template>
  <div class="chart-w sentiment-chart d-block w-100 relative">
    <div class="sentiment-chart__wrapper relative">
      <Apexchart
        v-if="!loading"
        :key="`${refreshConter}`"
        :options="chartOptions"
        :series="displayedSeries"
        :height="stylesChart.heightStackedHorizontalBarChartWithoutLegends"
      />
      <div class="sentiment-chart__legends">
        <div class="sentiment-chart__legends-item">
          <SadIcon />
          {{ t("sentiment_stat_bar.sentiment.negative") }}
        </div>
        <div class="sentiment-chart__legends-item">
          <NeutralIcon />
          {{ t("sentiment_stat_bar.sentiment.neutral") }}
        </div>
        <div class="sentiment-chart__legends-item">
          <GoodIcon />
          {{ t("sentiment_stat_bar.sentiment.positive") }}
        </div>
      </div>
    </div>
    <div class="sentiment-chart__menu">
      <div class="sentiment-chart__menu-left">
        <div class="sentiment-chart__title">
          {{ t('sentiment_stat_bar.title') }}
        </div>
      </div>
      <div class="sentiment-chart__menu-right">
        <RefreshIcon @click="loadData" />
        <CalendarInput v-model="date" />
        <FilterSelector
          v-model:selected-chart="selectedChart"
          :active-store="activeStore"
          :date="date"
          stop-load-on-clear
          @update-rubrics="updateRubrics"
          @clear-rubrics="clearRubrics"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, toRaw, ref, reactive, watch } from "vue";
import FilterSelector from "@/components/AnalyticsPage/AnalyticsGeneral/Charts/blocks/FilterSelector.vue";
import NeutralIcon from "@/components/Icons/NeutralIcon.vue";
import GoodIcon from "@/components/Icons/GoodIcon.vue";
import SadIcon from "@/components/Icons/SadIcon.vue";
import { useSentimentStatDynamicBlocksStore } from "@/store/Analytic/chart/sentiment-stat-dynamic-blocks";
import { useSentimentStatByThemeBlocksStore } from "@/store/Analytic/chart/sentiment-by-theme-stat-dynamic-blocks";
import VueApexCharts from "vue3-apexcharts";
import { stylesChart } from "@/tools/tooltipChart";
import useBarChart from "@/mixins/apexChart/barChart";
import { t } from "@/controllerLK/GlobusLocalization";
import useFilterDate from "@/mixins/apexChart/filterDate";
import CalendarInput from "@/components/AnalyticsPage/AnalyticsGeneral/Charts/blocks/CalendarInput.vue";
import RefreshIcon from "@/components/Icons/RefreshIcon.vue";

export default {
  name: "SentimentStatChart",
  components: {
    CalendarInput, Apexchart: VueApexCharts, FilterSelector, NeutralIcon, GoodIcon, SadIcon, RefreshIcon
  },
  setup() {
    const statStorage = useSentimentStatDynamicBlocksStore();
    const statByThemeStorage = useSentimentStatByThemeBlocksStore();

    const { date } = useFilterDate(loadData);
    const loading = ref(false);
  const selectedChart = ref(0);
  const newSeries = ref([]);
  const refreshConter = ref(0);
  const forceEmpty = ref(false);

    async function loadData() {
      await statStorage.loadStat([], date.value[0], date.value[1]);
      await statByThemeStorage.loadStat([], date.value[0], date.value[1]);
    }

    const activeStore = computed(() => {
      return selectedChart.value === 1 ? statStorage : statByThemeStorage;
    });

    const categories = computed(() => {
      return (activeStore.value.items || []).filter((item) => Object.values(item.data).length).map((item) => item.name);
    });

    const beforeUpdate = () => {
      loading.value = true;
      if (newSeries?.value?.length) {
        newSeries.value = [];
      }
    };

    watch(
        () => categories.value,
        () => updateRubrics(categories.value, true),
    );

    watch(
        () => selectedChart.value,
        () => updateRubrics(activeStore.value.categories, true),
    );

    const getValue = (index, data) => {
      const findTotal = (i) => {
        const period = (Object.values(data)[i]);
        if (period) {
          return Object.values(period.data).reduce((a, b) => (a + (b || 0)), 0);
        }
        return 0;
      };
      return data.map((item, i) => (item.data[index] | 0) / findTotal(i) * 100);
    };

    const series = computed(() => {
      const items = (activeStore.value.items || []).filter((item) => Object.values(item.data).length);

      return [
        {
          name: t("sentiment_stat_bar.sentiment.negative"),
          data: items ? getValue(-1, items) : [],
          color: "#FA665C"
        },
        {
          name: t("sentiment_stat_bar.sentiment.neutral"),
          data: items ? getValue(0, items) : [],
          color: "#7B87A5"
        },
        {
          name: t("sentiment_stat_bar.sentiment.positive"),
          data: items ? getValue(1, items) : [],
          color: "#C4F487"
        }
      ];
    });

    const chartOptions = reactive(useBarChart(
        {
          titleText: t("sentiment_stat_bar.title"),
          stacked: true,
        },
        activeStore?.value?.categories?.length ? activeStore.value.categories : categories.value,
        series.value,
        true
    ).chartOptions);

    const displayedSeries = computed(() => {
      if (forceEmpty.value) return [];
      if (newSeries.value.length) return newSeries.value;
      return series.value || [];
    });

    const clearRubrics = () => {
      loading.value = true;
      newSeries.value = [];
      forceEmpty.value = true;
      chartOptions.value = useBarChart(
          {
            noDataText: t("common_chart.no_data.short_text"),
            titleText: t("sentiment_stat_bar.title"),
            stacked: true,
          },
          activeStore.value?.categories || [],
          [],
          true
      ).chartOptions;
      refreshConter.value++;
      setTimeout(() => loading.value = false, 0);
    };

    const updateRubrics = (rubrics, newRefresh = false) => {
      forceEmpty.value = false;
      if (newRefresh) loading.value = true;
      const copiedCategories = JSON.parse(JSON.stringify(rubrics));
      const copiedSeries = JSON.parse(JSON.stringify(series.value));
      activeStore.value.categories = copiedCategories?.length ? copiedCategories : JSON.parse(JSON.stringify(categories.value));

      const indexes = activeStore.value.categories.map((item) => categories.value.indexOf(item));
      newSeries.value = copiedSeries.map((item) => {
        item.data = item.data.filter((n, i) => indexes.includes(i));
        return item;
      });

      chartOptions.value = useBarChart(
          {
            noDataText: t("common_chart.no_data.short_text"),
            titleText: t("sentiment_stat_bar.title"),
            stacked: true,
          },
          activeStore.value.categories || [],
          newSeries.value || [],
          true
      ).chartOptions;
      if (newRefresh) {
        refreshConter.value++;
        setTimeout(() => loading.value = false, 0);
      }
    };

    return { beforeUpdate, categories, chartOptions, clearRubrics, date, displayedSeries, loadData, loading, newSeries, series, stylesChart, selectedChart, activeStore, refreshConter, toRaw, t, updateRubrics };
  },
};
</script>

<style lang="scss" scoped>
.sentiment-chart {
  &__title {
    font-size: 16px;
    font-weight: 700;
    font-family: Roboto, sans-serif;
    line-height: 22px;
  }

  &__wrapper {
    z-index: 10;
  }

  &__menu {
    display: flex;
    position: absolute;
    align-items: center;
    top: 24px;
    height: 42px;
    width: 100%;
    left: 0;
    padding: 0 24px;
    justify-content: space-between;

    &-left {
      display: flex;
      gap: 20px;
    }

    &-right {
      display: flex;
      gap: 10px;
    }
  }

  &__legends {
    position: absolute;
    bottom: 25px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    margin: auto;

    &-item {
      display: flex;
      gap: 4px;
      align-items: center;
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }
}

.apexcharts-menu-open {
  margin-top: 15px;
}
</style>
